/**
 * Autorizzazione del cliente sulle attività non previste a contratto.
 *
 * Il lavoro fuori contratto si concorda prima di fatturarlo. Qui si segue il
 * giro: da sottoporre → sottoposto al cliente → approvato (o rifiutato), con
 * data, nota e il documento che fa fede (una mail, un ordine). Finché il
 * cliente non ha approvato, quell'importo non entra nella fattura.
 *
 * Le azioni sono riservate a noi: è un rapporto con il cliente. Al consulente
 * resta visibile lo stato, così sa a che punto è la sua attività aggiuntiva.
 */
import { useEffect, useState } from 'react';
import { salApi, allegatiApi } from '../api';

const fmtDate = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';
const kb = (n) => n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`;
const oggiIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const STATI = {
  da_sottoporre: { l: 'Da sottoporre al cliente', c: 'bg-gray-200 text-gray-700' },
  sottoposto:    { l: 'In attesa di risposta dal cliente', c: 'bg-amber-100 text-amber-800' },
  approvato:     { l: 'Autorizzata dal cliente', c: 'bg-green-100 text-green-700' },
  rifiutato:     { l: 'Non autorizzata dal cliente', c: 'bg-red-100 text-red-700' },
};

export default function AutorizzazioneExtra({ sal, staff, onCambiato }) {
  const stato = sal.extra_stato || 'da_sottoporre';
  const st = STATI[stato] || STATI.da_sottoporre;

  const [nota, setNota] = useState(sal.extra_nota || '');
  const [data, setData] = useState(oggiIso());
  // La motivazione di chi ha svolto il lavoro in più: è quella che portiamo
  // al cliente quando chiediamo l'autorizzazione.
  const [richiesta, setRichiesta] = useState(sal.extra_nota_consulente || '');
  const [allegati, setAllegati] = useState([]);
  const [salvando, setSalvando] = useState(false);

  const caricaAllegati = () => allegatiApi.list('sal', sal.id).then(setAllegati).catch(() => setAllegati([]));
  useEffect(() => { if (staff) caricaAllegati(); }, [sal.id, staff]);
  useEffect(() => { setNota(sal.extra_nota || ''); }, [sal.extra_nota]);

  const chiedi = async () => {
    setSalvando(true);
    try {
      await salApi.extraRichiesta(sal.id, richiesta);
      onCambiato?.();
    } catch (e) { alert(e); } finally { setSalvando(false); }
  };

  const cambia = async (nuovo) => {
    setSalvando(true);
    try {
      await salApi.extraStato(sal.id, { stato: nuovo, nota, data });
      onCambiato?.();
    } catch (e) { alert(e); } finally { setSalvando(false); }
  };

  const allega = async (file) => {
    if (!file) return;
    setSalvando(true);
    try {
      await allegatiApi.carica('sal', sal.id, file, 'autorizzazione_cliente');
      await caricaAllegati();
    } catch (e) { alert(e); } finally { setSalvando(false); }
  };

  const eliminaAllegato = async (a) => {
    if (!confirm(`Eliminare "${a.nome_file}"?`)) return;
    try { await allegatiApi.elimina(a.id); await caricaAllegati(); } catch (e) { alert(e); }
  };

  return (
    <div className={`border rounded-lg p-3 text-sm ${
      stato === 'approvato' ? 'bg-green-50 border-green-200'
      : stato === 'rifiutato' ? 'bg-red-50 border-red-200'
      : stato === 'sottoposto' ? 'bg-amber-50 border-amber-200'
      : 'bg-gray-50'}`}>

      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.c}`}>{st.l}</span>
        {sal.extra_data_invio && (
          <span className="text-xs text-gray-500">sottoposta il {fmtDate(sal.extra_data_invio)}</span>
        )}
        {sal.extra_data_esito && (
          <span className="text-xs text-gray-500">· risposta del {fmtDate(sal.extra_data_esito)}</span>
        )}
      </div>

      <p className="text-xs text-gray-600 mt-1.5">
        {stato === 'approvato'
          ? 'Le attività aggiuntive sono fatturabili: rientrano nell\'importo proposto in approvazione.'
          : stato === 'rifiutato'
            ? 'Il cliente non le ha autorizzate: restano documentate ma non verranno fatturate.'
            : 'Le attività aggiuntive NON entrano nell\'importo da fatturare finché il cliente non le autorizza.'}
      </p>

      {!staff && sal.extra_nota && (
        <p className="text-xs text-gray-600 mt-1">Nota di Sinthema: {sal.extra_nota}</p>
      )}

      {/* La motivazione del consulente: lui la scrive, noi la leggiamo e la
          usiamo per chiedere l'autorizzazione al cliente. */}
      {sal.extra_nota_consulente && (
        <div className="mt-2 bg-white/70 border rounded px-2.5 py-1.5">
          <div className="text-[11px] text-gray-500">
            Motivazione di chi ha svolto il lavoro
            {sal.extra_richiesta_il && <> · richiesta del {fmtDate(sal.extra_richiesta_il)}</>}
          </div>
          <div className="text-xs text-gray-700">{sal.extra_nota_consulente}</div>
        </div>
      )}

      {!staff && ['da_sottoporre', 'sottoposto'].includes(stato) && (
        <div className="mt-2.5">
          <label className="block text-xs font-medium text-gray-500 mb-0.5">
            Perché è stato necessario questo lavoro in più
            <span className="ml-1 font-normal text-gray-400">(lo sottoporremo al cliente)</span>
          </label>
          <textarea className="input py-1 text-sm" rows={2} value={richiesta}
                    placeholder="es. richiesto dal responsabile qualità durante l'audit: revisione delle procedure non prevista"
                    onChange={e => setRichiesta(e.target.value)} />
          <div className="flex items-center gap-2 mt-1.5">
            <button className="btn-primary btn-sm" disabled={salvando || !richiesta.trim()} onClick={chiedi}>
              {sal.extra_nota_consulente ? 'Aggiorna la richiesta' : 'Chiedi l\'autorizzazione'}
            </button>
            <span className="text-xs text-gray-500">
              {sal.extra_richiesta_il ? 'Sinthema l\'ha ricevuta.' : 'Sinthema la porterà al cliente.'}
            </span>
          </div>
        </div>
      )}

      {staff && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mt-2.5">
            <div className="sm:col-span-3">
              <label className="block text-xs font-medium text-gray-500 mb-0.5">
                Nota (come è stata concordata, con chi)
              </label>
              <input className="input py-1 text-sm" value={nota} onChange={e => setNota(e.target.value)}
                     placeholder="es. autorizzate via mail da Ing. Rossi il 12/09" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-0.5">Data</label>
              <input className="input py-1 text-sm" type="date" value={data}
                     onChange={e => setData(e.target.value)} />
            </div>
          </div>

          <div className="flex gap-1.5 mt-2 flex-wrap">
            {stato !== 'sottoposto' && (
              <button className="btn-secondary btn-sm" disabled={salvando}
                      onClick={() => cambia('sottoposto')}>📤 Sottoposta al cliente</button>
            )}
            {stato !== 'approvato' && (
              <button className="btn-sm btn bg-green-100 text-green-700 hover:bg-green-200 font-semibold"
                      disabled={salvando} onClick={() => cambia('approvato')}>✓ Autorizzata dal cliente</button>
            )}
            {stato !== 'rifiutato' && (
              <button className="btn-sm btn bg-red-100 text-red-600 hover:bg-red-200"
                      disabled={salvando} onClick={() => cambia('rifiutato')}>✕ Non autorizzata</button>
            )}
            {stato !== 'da_sottoporre' && (
              <button className="btn-secondary btn-sm" disabled={salvando}
                      onClick={() => cambia('da_sottoporre')} title="Riporta allo stato iniziale">↺</button>
            )}
          </div>

          {/* Il documento che fa fede: la mail di conferma, l'ordine, la PEC. */}
          <div className="mt-2.5 border-t pt-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-500">Documenti di autorizzazione</span>
              <input className="text-xs" type="file" accept="image/*,application/pdf,.eml,.msg"
                     onChange={e => { allega(e.target.files?.[0]); e.target.value = ''; }} />
            </div>
            {allegati.length === 0
              ? <p className="text-xs text-gray-400 mt-1">Nessun documento allegato.</p>
              : (
                <ul className="mt-1 space-y-0.5">
                  {allegati.map(a => (
                    <li key={a.id} className="flex items-center gap-2 text-xs">
                      <button className="text-brand-600 hover:underline"
                              onClick={() => allegatiApi.scarica(a.id, a.nome_file).catch(e => alert(e))}>
                        📎 {a.nome_file}
                      </button>
                      <span className="text-gray-400">{kb(a.dimensione || 0)}</span>
                      <button className="text-gray-300 hover:text-red-600 ml-auto"
                              title="Elimina" onClick={() => eliminaAllegato(a)}>🗑</button>
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </>
      )}
    </div>
  );
}
